import React from "react";
import { useTranslation } from "react-i18next";
import {
  TextField,
  MenuItem,
  Button,
  Typography,
  Divider,
  IconButton,
  Tooltip,
  FormHelperText,
  Skeleton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { getFloorOptions } from "../../../helpers";
import AssessmentMetaFields from "../../../shared/AssessmentMetaFields";
import RiskItem from "../riskItem";
import { usePolygonDrawContext } from "../../polygonDraw";
import {
  DRAW_MODES,
  formatArea,
  formatPerimeter,
} from "../../polygonDraw/helpers";
import { useAssessmentPdfExport } from "../history/useAssessmentPdfExport";
import { MAP_STYLE } from "../../constants";

import styles from "./polygon.module.css";

function PolygonAssessmentTabContent({ mapRef, selectedEarthquakeLayers }) {
  const { t } = useTranslation();
  const {
    drawRef,
    polygonMetrics,
    polygonCount,
    totalArea,
    totalPerimeter,
    isMeasuring,
    measureError,
    isOverAreaLimit,
    maxTotalArea,
    floor,
    setFloor,
    name,
    setName,
    notes,
    setNotes,
    results,
    isSubmitting,
    submitError,
    sendAssessment,
    clearResults,
    reviseAssessment,
    onDrawChange,
    mode,
  } = usePolygonDrawContext();

  // Block submission mid-drawing (Draw area toolbar button active).
  const isDrawing = mode === DRAW_MODES.DRAW;

  const {
    exportAssessmentPdf,
    activeExportKey,
    errorMessage: pdfErrorMessage,
    exportScopeCurrent,
  } = useAssessmentPdfExport();

  // Totals come from the measure API; while a measurement is in flight they
  // render as skeletons, and as dashes when the measurement failed.
  const renderTotal = (value, formatter) => {
    if (isMeasuring)
      return (
        <Skeleton
          variant="text"
          width={90}
          sx={{ display: "inline-block", verticalAlign: "middle" }}
        />
      );
    if (measureError || value == null) return "—";
    return formatter(value, t);
  };

  const getCurrentMapStyle = () => {
    const map = mapRef?.current?.getMap?.();
    const currentSprite = map?.getStyle?.()?.sprite || "";
    return currentSprite.includes("satellite")
      ? MAP_STYLE.satellite
      : MAP_STYLE.street;
  };

  // Results view — same RiskItem list as the point flow, no map marker/popup.
  if (results) {
    return (
      <div className={styles.container}>
        <div className={styles.buttonRow}>
          <Tooltip title={t("risksMap.polygon.newAssessment")}>
            <Button variant="outlined" fullWidth onClick={clearResults}>
              {t("risksMap.polygon.newAssessmentShort")}
            </Button>
          </Tooltip>

          <Tooltip title={t("risksMap.polygon.adjustReassess")}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={reviseAssessment}
            >
              {t("risksMap.polygon.adjustReassessShort")}
            </Button>
          </Tooltip>
        </div>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={() =>
            exportAssessmentPdf({
              assessment: results,
              mapStyle: getCurrentMapStyle(),
              earthquakeLayers: selectedEarthquakeLayers,
              scopeKey: exportScopeCurrent,
            })
          }
          disabled={activeExportKey === exportScopeCurrent}
        >
          {activeExportKey === exportScopeCurrent
            ? t("risksMap.pdf.exporting")
            : t("risksMap.pdf.download")}
        </Button>

        {pdfErrorMessage ? (
          <FormHelperText error>{pdfErrorMessage}</FormHelperText>
        ) : null}

        <Divider />

        <RiskItem
          name="Fire"
          key="fire"
          risk={results.fire?.risk}
          message={results.fire?.message}
          description={results.fire?.description}
          descriptionDict={results.fire?.description_dict}
          riskType="fire"
        />
        <RiskItem
          name="Flood"
          key="flood"
          risk={results.flood?.risk}
          message={results.flood?.message}
          description={results.flood?.description}
          descriptionDict={results.flood?.description_dict}
          riskType="flood"
        />
        <RiskItem
          name="Earthquake"
          key="earthquake"
          risk={results.earthquake?.risk}
          message={results.earthquake?.message}
          description={results.earthquake?.description}
          descriptionDict={results.earthquake?.description_dict}
          riskType="earthquake"
        />
      </div>
    );
  }

  const handleDeletePolygon = (id) => {
    const draw = drawRef.current;
    if (!draw) return;
    draw.delete(id);
    // draw.delete() does not emit a draw.delete event, so refresh manually.
    onDrawChange();
  };

  return (
    <div className={styles.container}>
      <Typography variant="body2" color="text.secondary">
        {t("risksMap.polygon.intro")}
      </Typography>

      <TextField
        select
        label={t("forms.floor")}
        variant="outlined"
        fullWidth
        size="small"
        value={floor}
        onChange={(e) => setFloor(e.target.value)}
        disabled={isSubmitting}
      >
        {getFloorOptions(t).map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <AssessmentMetaFields
        name={name}
        notes={notes}
        onNameChange={setName}
        onNotesChange={setNotes}
        disabled={isSubmitting}
      />

      <div>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("risksMap.polygon.listTitle")} ({polygonCount})
        </Typography>

        {polygonCount > 1 ? (
          <div className={styles.totals}>
            <Typography variant="caption" color="text.secondary">
              {t("risksMap.polygon.totalAreaLabel")}:{" "}
              {renderTotal(totalArea, formatArea)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("risksMap.polygon.totalPerimeterLabel")}:{" "}
              {renderTotal(totalPerimeter, formatPerimeter)}
            </Typography>
          </div>
        ) : null}

        {polygonCount === 0 ? (
          <div className={styles.empty}>
            <Typography variant="body2" color="text.secondary">
              {t("risksMap.polygon.noPolygons")}
            </Typography>
          </div>
        ) : (
          <div className={styles.list}>
            {polygonMetrics.map((metric, index) => (
              <div key={metric.id} className={styles.row}>
                <div className={styles.rowText}>
                  <Typography variant="body2" fontWeight={600}>
                    {t("risksMap.polygon.polygonLabel")} {index + 1}
                  </Typography>
                  {metric.isMeasuring ? (
                    <Skeleton variant="text" width={140} />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t("risksMap.polygon.areaLabel")}:{" "}
                      {metric.hasError ? "—" : formatArea(metric.area, t)} ·{" "}
                      {t("risksMap.polygon.perimeterLabel")}:{" "}
                      {metric.hasError
                        ? "—"
                        : formatPerimeter(metric.perimeter, t)}
                    </Typography>
                  )}
                </div>
                <Tooltip title={t("risksMap.polygon.tools.delete")}>
                  <IconButton
                    size="small"
                    onClick={() => handleDeletePolygon(metric.id)}
                    aria-label={t("risksMap.polygon.tools.delete")}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            ))}
          </div>
        )}
      </div>

      {isOverAreaLimit ? (
        <FormHelperText
          error
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          {t("risksMap.polygon.areaLimitExceeded", {
            limit: formatArea(maxTotalArea, t),
            total: formatArea(totalArea, t),
          })}
        </FormHelperText>
      ) : null}

      {measureError ? (
        <FormHelperText
          error
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          {t(
            measureError?.error_code === "invalid_geometry"
              ? "errors.invalidGeometry"
              : "errors.measureFailed",
          )}
        </FormHelperText>
      ) : null}

      <Button
        variant="contained"
        color="primary"
        fullWidth
        disabled={
          polygonCount === 0 ||
          isSubmitting ||
          isOverAreaLimit ||
          isDrawing ||
          isMeasuring ||
          !!measureError
        }
        onClick={sendAssessment}
      >
        {isSubmitting
          ? t("common.loading")
          : t("risksMap.polygon.sendAssessment")}
      </Button>

      {submitError ? (
        <FormHelperText error>
          {submitError.startsWith("errors.") ? t(submitError) : submitError}
        </FormHelperText>
      ) : null}
    </div>
  );
}

export default PolygonAssessmentTabContent;
